import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const TestimonialsSection = () => {
  const { t } = useLanguage();

  const testimonials = [
    {
      textKey: 'testimonial_1_text',
      authorKey: 'testimonial_1_author',
      roleKey: 'testimonial_1_role',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1687422808191-93810cd07ab0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBzaG9wfGVufDB8fHx8MTc3NjQyMTQ1MXww&ixlib=rb-4.1.0&q=85',
    },
    {
      textKey: 'testimonial_2_text',
      authorKey: 'testimonial_2_author',
      roleKey: 'testimonial_2_role',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1719563014656-802c0aa9632a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBzaG9wfGVufDB8fHx8MTc3NjQyMTQ1MXww&ixlib=rb-4.1.0&q=85',
    },
    {
      textKey: 'testimonial_3_text',
      authorKey: 'testimonial_3_author',
      roleKey: 'testimonial_3_role',
      rating: 5,
      image: null,
    }
  ];

  return (
    <section
      className="py-24 md:py-32 bg-[#1e3a5f]"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
            {t('testimonials_title') || 'What Our Customers Say'}
          </h2>
          <p className="text-slate-400 text-lg">
            {t('testimonials_subtitle') || 'Trusted by shop owners and freelancers across Spain'}
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.authorKey}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 relative"
              data-testid={`testimonial-${index + 1}`}
            >
              {/* Quote Icon */}
              <Quote className="w-10 h-10 text-[#22c55e] opacity-50 mb-4" />

              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>

              {/* Text */}
              <p className="text-white/90 text-lg mb-6 leading-relaxed">
                {t(testimonial.textKey)}
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                {testimonial.image ? (
                  <img
                    src={testimonial.image}
                    alt={t(testimonial.authorKey)}
                    className="w-12 h-12 object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-[#22c55e] flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {t(testimonial.authorKey).charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-white font-semibold">
                    {t(testimonial.authorKey)}
                  </p>
                  <p className="text-white/60 text-sm">
                    {t(testimonial.roleKey)}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
